import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BookingMode, IDisplayState, ISetActiveBookingOnDateChange } from './types';
import {
  BookingMethods,
  BookingStatusNames,
  BookingType,
  IBookedByCustomerDetail,
  IBooking,
  IBookingLink,
  IBookingMenuOption,
  IBookingStatus,
  IBookingTag,
  ICustomer,
  IDiarySchedule,
  IDiaryScheduleRawTimes,
  IDiaryService,
  IPaymentDetailsBase,
  IScheduleLookup,
  IServiceItem,
  ITableItem,
  PaymentTypes,
  preAuthStatus,
  PricingTypes,
  SECTION_ANY_ID,
  servicePaymentType,
  StatusUnconfirmed,
  TypeBookingCustomerProps,
} from 'shared-types/index';
import { rootDisplayActions } from 'app/reducers/display/slice';
import { SlidingStatesEnum } from 'app/reducers/display/types';
import { validationActions } from 'booking-creator/reducers/validation/slice';
import { SaveStatus } from 'booking-creator/reducers/validation/types';
import { resetReducers } from 'booking-creator/hooks';
import { prefillPayment } from 'booking-creator/reducers/payment/slice';
import { DiaryRootState } from 'app/appRoot/rootReducer';
import { cloneDeep } from 'lodash-es';
import ServicesLookupService from 'external-services/services-lookup-service';
import LayoutsService from 'external-services/layouts-service/layouts.service';
import TablesLookupService from 'external-services/tables-lookup-service/tablesLookup.service';
import BookingLookupService from 'external-services/booking-lookup-service';
import BookingChangeService from 'external-services/booking-change-service/bookingChange.service';
import { IBookingChangeState } from 'external-services/booking-change-service/type';
import DateUtilsService from 'external-services/date-utils-service';
import ScheduleLookupService from 'external-services/schedule-lookup-service/scheduleLookup.service';
import SectionsService from 'external-services/sections-service';
import { tablesActions } from 'booking-creator/reducers/tables/slice';
import { activeSectionsActions } from 'booking-creator/reducers/activeSections/slice';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import store from 'appRoot/Store';
import BookingUpdateService from 'external-services/booking-update-service';
import { CONTACT_MSG, PROBLEMS_MSG, REFRESH_MSG } from 'appRoot/app-error-messages';
import BookingNormaliseService from 'external-services/booking-normalise-service/bookingNormalise.service';
import { addToScheduleLookup } from 'app/reducers/schedule/slice';
import { IServiceListItem } from 'booking-creator/components/RepeatBooking/types';
import TableJoinService from 'booking-creator/services/tableJoin/tableJoin.service';

const NS = 'displaySlice';

//TODO: update values for initial state
const initialState: IDisplayState = {
  heading: '',
  tabVis: null,
  finalConfirmation: false,
  isLoading: false,
  mode: BookingMode.idle,
  activeBooking: null,
  backupActiveBooking: null,
  activeBookingChanges: null,
  slideOut: false,
  originalBooking: null,
  durationManuallyEdited: false,
  hasCustomBookingDuration: false,
  selectedService: null,
  activeServiceViewList: [],
  activeSchedule: null,
  isBookingLoaded: false,
  isDateToday: false,
  isManualCopyOriginalBooking: false,
  isManualPax: false,
  bookingIdForEdit: '',
};

export const createNewActiveBooking = createAsyncThunk(
  'display/createNewActiveBooking',
  async (payload: {
    method: BookingMethods;
    time: Date;
    duration: number;
    tableId: string;
    sectionId: string;
    layoutId: string;
    selectedService?: IServiceItem;
    skipDefaultSelection?: boolean;
  }, { dispatch, getState }): Promise<Partial<IBooking>> => {
    const state = getState() as DiaryRootState;
    const {
      activeSectionsReducer: { selectedTableIds },
      venueReducer: {timeZoneId},
      layoutReducer: {couchReducedLayouts},
      serviceReducer: {currentServiceViews}
    } = state;

    const bookedAt = DateUtilsService.getVenueTime(timeZoneId);

    /**
     * Duration needs to default to zero so that partial durations on floorplan can be detected in
     * EditableBookingDetails
     */
    const duration = payload.duration || 0;
    let tables: ITableItem[] = [];

    if (payload.tableId) {
      const tablesList = TablesLookupService.getTablesLookup();
      const tableItem = tablesList.get(payload.tableId);
      if (tableItem) {
        tables = [tableItem];
      }
      if (!payload.selectedService && currentServiceViews && payload.layoutId) {
        const filteredService = currentServiceViews.filter((serviceView) => {
          return (serviceView.service as IServiceItem)?.sectionLayouts?.some((sectionLayout) => {
            return sectionLayout.sectionId === payload.sectionId && sectionLayout.layoutId === payload.layoutId;
          })
        })

        const finalFilteredService = filteredService.filter((serviceView) => {
          return (serviceView.service as IServiceItem)?. startTime <= payload.time && (serviceView.service as IServiceItem)?.endTime >= payload.time;
        })

        if (finalFilteredService.length) {
          // choose the last service here as legacy choose the last one
          payload.selectedService = finalFilteredService[finalFilteredService.length-1].service as IServiceItem;
        }
      }
    }

    const newBooking: Partial<IBooking> = {
      _id: null, // populated on save
      time: payload.time,
      duration,
      people: 2,
      serviceName: '',
      tables,
      preferredSectionId: payload.sectionId || SECTION_ANY_ID,
      paymentDetails: {
        paymentType: servicePaymentType.noPayment,
        price: 0,
        menuOptions: [],
        peopleRequired: 1,
        singleMenuPerBooking: false,
      },
      status: StatusUnconfirmed,
      tags: [],
      bookingType: BookingType.Booking,
      locked: false,
      lastModifiedDate: bookedAt.toString(),
      orgTime: bookedAt.toString(),
      bookingRef: '',
      phone: '',
      lastName: '',
      firstName: '',
      email: '',
      bookingEnd: DateUtilsService.addMinutes(bookedAt, duration),
      bookedAt,
      notes: '',
      customerId: '',
      method: payload.method,
      // the rest will be populated by user (Partial<IBooking>)
    }
    if (payload.selectedService) {
      newBooking.serviceId = payload.selectedService._id;
      newBooking.serviceName = payload.selectedService.name;
      newBooking.serviceType = payload.selectedService.type;
    }
    dispatch(tablesActions.setHasUserDefinedTables(Boolean(tables.length)))
    return newBooking;
  }
);

export const tempSeatNow = createAsyncThunk(
  'display/tempSeatNow',
  async (_, { dispatch, getState }): Promise<any> => {
    const state = getState() as DiaryRootState;
    const {
      activeSectionsReducer: { selectedTableIds },
    } = state;
    await dispatch(setActiveBookingTablesFromIds({ selectedTableIds }));
    dispatch(activeSectionsActions.setSelectedTableIds(selectedTableIds));
  }
);

export const updateActiveBookingStatus = createAsyncThunk(
  'display/updateActiveBookingStatus',
  async (
    payload: { name: string; isManualUpdate: boolean; booking?: Partial<IBooking> },
    { dispatch, getState }
  ): Promise<{ status: IBookingStatus; isManualUpdate: boolean }> => {
    // @todo: figure out how to make this reusable amongst different apps. Severity: high
    const { statusReducer } = getState() as DiaryRootState;

    const status = statusReducer.fullStatusOptions.find((s) => s.name === payload.name);
    const isManualUpdate = payload.isManualUpdate;

    return { status, isManualUpdate };
  }
);

const getCachedSchedule = (bookingDate: Date, scheduleLookup?: IScheduleLookup): IDiarySchedule => {
  if (!scheduleLookup) {
    return null;
  }

  const dateId = DateUtilsService.dateToIdStr(bookingDate);
  const cachedSchedule = scheduleLookup[dateId];
  if (cachedSchedule) {
    return cachedSchedule.schedule;
  }
  return null;
};

const getScheduleByDate = async (
  schedules: IDiaryScheduleRawTimes[],
  bookingDate: Date,
  timezoneId?: string
): Promise<IDiarySchedule | null> => {
  const schedule = schedules.find((s) => s.dayNum === bookingDate.getDay());

  const mutatbleSchedule: IDiaryScheduleRawTimes = cloneDeep(schedule);
  const isValidBookingDate = DateUtilsService.isValidDate(bookingDate);

  let newSchedule: IDiarySchedule;

  if (!isValidBookingDate) return null;

  bookingDate = DateUtilsService.getClearDate(bookingDate, null);

  try {
    newSchedule = await ScheduleLookupService.addExtrasToSchedule(
      mutatbleSchedule,
      bookingDate,
      null,
      timezoneId
    );
  } catch (err) {
    console.warn(
      NS,
      'getScheduleByDate',
      `Error calling "ScheduleLookupService.addExtrasToSchedule"`,
      err
    );
    store.dispatch(
      errorStateActions.setErrorState({
        errorTitle: 'Problem adding additional information to the schedule',
        errorMessage: `There was an error while adding service, event, blockout and section details to the schedule for the date selected. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
        accordionDetail: getAccordionDetail(err),
      })
    );
  }
  return newSchedule;
};

const getServiceViewList = (schedule: IDiarySchedule): IServiceListItem[] => {
  let serviceViewList: IServiceListItem[] = [];
  if (schedule && schedule.services && schedule.isOpen) {
    serviceViewList = ServicesLookupService.buildServiceViewList(
      schedule.serviceLayouts,
      schedule.services
    ).serviceViewList as IServiceListItem[];
  }
  return serviceViewList;
};

const getSelectedService = (
  newDateSchedule: IDiarySchedule,
  serviceViewList: IServiceListItem[],
  timeZoneId: string,
  originalBooking: Partial<IBooking>,
  newBooking: Partial<IBooking>,
  selectedDate: Date
): IDiaryService => {
  let selectedService: IDiaryService;

  /**
   * 1st: try to find service ID.
   */
  selectedService = serviceViewList.find((serviceViewItem) => {
    return serviceViewItem.service._id === originalBooking?.serviceId;
  })?.service;

  /**
   * 2nd: try to find same service name
   */
  if (!selectedService) {
    selectedService = serviceViewList.find((serviceViewItem) => {
      return serviceViewItem.service.name === originalBooking?.serviceName;
    })?.service;
  }
  /**
   * 3rd: try to find nearest time service
   */
  if (!selectedService) {
    try {
      const time = originalBooking?.time || newBooking.time;
      selectedService = ServicesLookupService.selectService(
        newDateSchedule,
        null,
        timeZoneId,
        time,
        true,
        undefined
      );
    } catch (err) {
      console.warn(
        NS,
        'getSelectedService',
        `failed to load 'ServicesLookupService.selectService'`,
        err
      );
    }
  }
  return selectedService;
};

export const setServiceIdForActiveBooking = createAsyncThunk(
  'display/setServiceIdForActiveBooking',
  async (
    payload: { bookingDate: Date },
    { dispatch, getState }
  ): Promise<{
    selectedService: IDiaryService;
    activeServiceViewList: IServiceListItem[];
    activeSchedule: IDiarySchedule;
    preferredSectionId: string;
  }> => {
    const { displayReducer, scheduleReducer, venueReducer } = getState() as DiaryRootState;
    const { couchReducedSchedules, scheduleLookup, selectedDate } = scheduleReducer;
    const { activeBooking, mode } = displayReducer;
    const { timeZoneId } = venueReducer;
    const newBooking: Partial<IBooking> = cloneDeep(activeBooking);
    newBooking.tables = null;

    let schedule: IDiarySchedule = getCachedSchedule(payload.bookingDate, scheduleLookup);
    if (!schedule) {
      schedule = await getScheduleByDate(couchReducedSchedules, payload.bookingDate, timeZoneId);
      if (schedule) {
        await dispatch(addToScheduleLookup({ schedule, date: payload.bookingDate }));
      }
    }
    const activeServiceViewList = getServiceViewList(schedule);

    let selectedService: IDiaryService = {} as IDiaryService;
    if (schedule?.services && schedule?.isOpen) {
      selectedService = getSelectedService(
        schedule,
        activeServiceViewList,
        timeZoneId,
        newBooking,
        newBooking,
        selectedDate
      );
    }

    let preferredSectionId: string = SECTION_ANY_ID;
    if (mode === BookingMode.newBooking && activeBooking.preferredSectionId) {
      preferredSectionId = activeBooking.preferredSectionId;
    } else if ((mode === BookingMode.editBooking || mode === BookingMode.viewBooking) && activeBooking.primaryTableId) {
      preferredSectionId = SectionsService.getSectionFromPrimaryTable(
        activeBooking.primaryTableId,
        activeBooking.tables
      );
    }

    const isDateToday = DateUtilsService.isDateToday(newBooking.time, venueReducer.timeZoneId);
    dispatch(actions.setIsDateToday(isDateToday));

    return { selectedService, activeSchedule: schedule, preferredSectionId, activeServiceViewList };
  }
);

export const setActiveBookingOnDateChange = createAsyncThunk(
  'display/setActiveBookingOnDateChange',
  async (
    payload: { bookingDate: Date },
    { dispatch, getState }
  ): Promise<ISetActiveBookingOnDateChange> => {
    const { displayReducer, scheduleReducer, venueReducer, layoutReducer } =
      getState() as DiaryRootState;
    const { couchReducedSchedules, scheduleLookup, selectedDate } = scheduleReducer;
    const { couchReducedLayouts } = layoutReducer;
    const { activeBooking, originalBooking } = displayReducer;
    const { timeZoneId } = venueReducer;
    const newBooking: Partial<IBooking> = cloneDeep(activeBooking);
    newBooking.tables = null;

    let schedule: IDiarySchedule = getCachedSchedule(payload.bookingDate, scheduleLookup);
    if (!schedule) {
      schedule = await getScheduleByDate(couchReducedSchedules, payload.bookingDate, timeZoneId);
      if (schedule) {
        await dispatch(addToScheduleLookup({ schedule, date: payload.bookingDate }));
      }
    }
    const activeServiceViewList = getServiceViewList(schedule);

    let selectedService: IDiaryService;

    if (schedule?.services && schedule?.isOpen) {
      selectedService = getSelectedService(
        schedule,
        activeServiceViewList,
        timeZoneId,
        originalBooking ?? newBooking,
        newBooking,
        selectedDate
      );

      const layoutIds = LayoutsService.getLayoutIds(selectedService);

      const { preferredSectionId, tablesList } = await TablesLookupService.getTablesByLayoutIds(
        layoutIds,
        payload.bookingDate,
        newBooking.preferredSectionId
      );
      newBooking.preferredSectionId = preferredSectionId;
      const tables = couchReducedLayouts.find((l) => l.sectionId === preferredSectionId)?.tables;
      const tableIdSelected = tables?.includes(activeBooking.primaryTableId);
      //------------If selected table name is not same as last table name, then do an auto select----------------
      if (!tableIdSelected) {
        newBooking.primaryTableId = BookingLookupService.getPrimaryTableId(newBooking as IBooking);
      }
      const existingTables = tablesList.filter((t1) =>
        activeBooking.tables.some((t2) => t2._id === t1._id)
      );
      newBooking.tables = existingTables?.length ? existingTables : [tablesList[0]];
      //@todo add bookingCalculations call from booking-availability-service, once UI-225 is finished
      // newBooking.tables = tablesList;
      newBooking.serviceId = selectedService?._id;
      newBooking.serviceType = selectedService?.type;
      newBooking.serviceName = selectedService?.name;
      newBooking.paymentDetails = (activeBooking.paymentDetails || activeBooking.paymentPending
        ? cloneDeep(activeBooking.paymentDetails || activeBooking.paymentPending)
        : { paymentType: PaymentTypes.NoPayment }) as any as IPaymentDetailsBase;
      // reset menu options
      newBooking.selectedMenuOptions = [];
      newBooking.selectedUpsellOptions = [];
    }

    const isDateToday = DateUtilsService.isDateToday(newBooking.time, venueReducer.timeZoneId);
    dispatch(actions.setIsDateToday(isDateToday));

    return { newBooking, selectedService, activeServiceViewList, activeSchedule: schedule };
  }
);

export const setActiveBookingTablesFromIds = createAsyncThunk(
  'display/setActiveBookingTablesFromIds',
  async (
    payload: {
      selectedTableIds: string[];
      tablesListWithJoins?: ITableItem[];
      isManualJoin?: boolean;
    },
    { dispatch, getState }
  ): Promise<{ tables: ITableItem[]; preferredSectionId: string }> => {
    const { displayReducer, scheduleReducer, venueReducer } = getState() as DiaryRootState;
    const { couchReducedSchedules, scheduleLookup, selectedDate } = scheduleReducer;
    const { activeBooking, mode } = displayReducer;
    const { timeZoneId } = venueReducer;

    const bookingDate = activeBooking.time || selectedDate;
    let selectedTables: ITableItem[] = [];
    let newPreferredSectionId = SECTION_ANY_ID;

    let schedule: IDiarySchedule = getCachedSchedule(bookingDate, scheduleLookup);
    if (!schedule) {
      schedule = await getScheduleByDate(couchReducedSchedules, bookingDate, timeZoneId);
    }
    const activeServiceViewList = getServiceViewList(schedule);

    let selectedService: IDiaryService;

    if (schedule?.services && schedule?.isOpen) {
      selectedService = getSelectedService(
        schedule,
        activeServiceViewList,
        timeZoneId,
        activeBooking,
        activeBooking,
        selectedDate
      );

      const layoutIds = LayoutsService.getLayoutIds(selectedService);

      const { preferredSectionId, tablesList } = await TablesLookupService.getTablesByLayoutIds(
        layoutIds,
        bookingDate,
        activeBooking.preferredSectionId
      );

      newPreferredSectionId = preferredSectionId;
      const tablesListWithJoins = payload.tablesListWithJoins || tablesList;

      if (mode === BookingMode.newBooking) {
        // when creating a new booking, we need to split the table join into it's individual tables
        selectedTables = TableJoinService.convertToManualJoin(
          payload.selectedTableIds,
          tablesListWithJoins,
          payload.isManualJoin || false
        );
      } else {
        // when using edit booking, the tables won't include table joins. All tables will be manual joins.
        selectedTables = tablesListWithJoins.filter((t) =>
          payload.selectedTableIds.includes(t._id)
        );
      }
    }

    if (selectedTables?.length) {
      // removes from WL if it has been added
      dispatch(setBookingForWaitListOrSBL({ doAdd: false, isWaitList: true }));
    }

    return { tables: selectedTables, preferredSectionId: newPreferredSectionId };
  }
);

export const getBookingChanges = createAsyncThunk(
  'display/getBookingChanges',
  async (payload: { booking: IBooking }, { dispatch, getState }): Promise<IBookingChangeState> => {
    const booking: IBooking = payload.booking;

    return new Promise((resolve, reject) => {
      if (booking) {
        BookingChangeService.getChanges(booking)
          .then((res: IBookingChangeState) => {
            resolve(res);
          })
          .catch((err: any) => {
            reject(err);
          });
      } else {
        resolve(null);
      }
    });
  }
);
/**
 * this function for update partial booking, carefully when use this function. no data validation, and need update _rev
 */
export const updatePartialBooking = createAsyncThunk(
  'display/updatePartialBooking',
  async (
    payload: Partial<IBooking>,
    { dispatch, getState }
  ): Promise<{
    id: string; //booking_2022-11-01_O7TS8KBL9TBGU_1667270231793
    ok: boolean; // true
    rev: string; //5-65bb4b81fc609b7219168a32b3101cc3
  }> => {
    const activeBooking = (getState() as DiaryRootState).displayReducer.activeBooking;
    // use Date from id to get booking data
    const bookingDate = DateUtilsService.getDateFromBookingId(activeBooking._id);
    const databaseBooking = await BookingLookupService.getById(activeBooking._id, bookingDate);
    const newBooking = cloneDeep({
      ...databaseBooking,
      ...payload,
    });
    try {
      return await BookingUpdateService.updateInCouchOrPouchWhenOffline(newBooking as any, null);
    } catch (e) {
      return null;
    }
  }
);
export const setService = createAsyncThunk(
  'display/setService',
  (
    payload: { service: IServiceItem; isNoCalculatePayment?: boolean },
    { dispatch, getState }
  ): {
    activeService: IServiceItem;
    preferredSectionId: string;
  } => {
    const { displayReducer } = getState() as DiaryRootState;
    const { activeBooking, mode } = displayReducer;

    !payload.isNoCalculatePayment &&
      dispatch(
        prefillPayment({
          selectedService: payload.service,
          selectedPeople: activeBooking.people,
        })
      );

    const sectionExistsInService = payload.service.sectionLayouts.some(sl => {
      if (sl.sectionClosed || !sl.layoutId || sl.sectionNotUsed) {
        return false;
      }
      return sl.sectionId === activeBooking.preferredSectionId;
    })

    const preferredSectionId: string = sectionExistsInService ? activeBooking.preferredSectionId : SECTION_ANY_ID;
    return { activeService: payload.service, preferredSectionId };
  }
);

export const setBookingPeople = createAsyncThunk(
  'display/setBookingPeople',
  (
    payload: { people: number; isManualPax?: boolean },
    { dispatch, getState }
  ): { people: number; isManualPax: boolean } => {
    const { displayReducer } = getState() as DiaryRootState;
    const { selectedService } = displayReducer;

    dispatch(
      prefillPayment({
        selectedService: selectedService,
        selectedPeople: payload.people,
      })
    );

    return { people: payload.people, isManualPax: !!payload.isManualPax };
  }
);

export const setBookingForWaitListOrSBL = createAsyncThunk(
  'display/setBookingForWaitListOrSBL',
  (payload: { doAdd: boolean; isWaitList: boolean }, { dispatch, getState }): {
    bookingType: BookingType, tables: ITableItem[]
  } => {
    const { tablesReducer, displayReducer } = getState() as DiaryRootState;

    let bookingType = displayReducer.activeBooking.bookingType;
    let tables = displayReducer.activeBooking.tables;

    if (payload.doAdd) {
      if (payload.isWaitList) {
        dispatch(tablesActions.setArchivedWaitListTables(tables));
      }
      bookingType = payload.isWaitList
        ? BookingType.WaitList
        : BookingType.StandbyList;
      tables = [];
      if (payload.isWaitList) {
        dispatch(activeSectionsActions.setSelectedTableIds([])); // needs this to clear the table seater ui
      }
    } else {
      bookingType = BookingType.Booking;
      if (payload.isWaitList) {
        tables = tablesReducer.archivedWaitListTables;
      }
      dispatch(
        activeSectionsActions.setSelectedTableIds(
          tablesReducer.archivedWaitListTables.map((t) => t._id)
        )
      );
      if (payload.isWaitList) {
        dispatch(tablesActions.setArchivedWaitListTables([]));
      }
    }
    return {bookingType, tables};
  }
);

export const setCustomerDetailsOnActiveBooking = createAsyncThunk(
  'display/setCustomerDetailsOnActiveBooking',
  (
    payload: { customer: ICustomer },
    { dispatch, getState }
  ): {
    activeBooking: Partial<IBooking>;
  } => {
    const { displayReducer } = getState() as DiaryRootState;
    const activeBooking = cloneDeep(displayReducer.activeBooking);

    activeBooking.firstName = payload.customer?.firstName || '';
    activeBooking.lastName = payload.customer?.lastName || '';
    activeBooking.email = payload.customer?.email || '';
    activeBooking.phone = payload.customer?.phone || '';
    activeBooking.company = payload.customer?.company || '';
    activeBooking.tags =
      displayReducer.mode === BookingMode.newBooking
        ? [...(activeBooking.tags ?? []), ...(payload.customer?.tags ?? [])]
        : activeBooking.tags; // Adding customer tags
    // activeBooking.phone2 = payload.customer?.firstName || '';

    return { activeBooking };
  }
);

let debounceActiveBookingFn: NodeJS.Timeout;
export const exitSlide = createAsyncThunk(
  'display/exit',
  (payload: { isCloseBtn: boolean, slideOutCompleteCB?: () => void }, { dispatch, getState }): void => {
    const { displayReducer, rootDisplayReducer, paymentReducer } = getState() as DiaryRootState;
    const activeBooking = cloneDeep(displayReducer.activeBooking);
    const isNewMode = displayReducer.mode === BookingMode.newBooking;
    const isEditMode = displayReducer.mode === BookingMode.editBooking;
    const slideState = rootDisplayReducer.slideState;
    const hasPaymentForActiveBooking =
      activeBooking?.paymentDetails?.paymentType !== servicePaymentType.noPayment;
    const hasPayment = !payload.isCloseBtn && hasPaymentForActiveBooking;
    const isStandbyBooking = activeBooking.bookingType === BookingType.StandbyList;
    const isSetActiveBookingInBookingCreator = paymentReducer.isSetActiveBookingInBookingCreator;
    const isPaymentPriceDirty = paymentReducer.isPaymentPriceDirty;
    const changeNoPaymentToOtherPayment =
      hasPaymentForActiveBooking &&
      displayReducer?.originalBooking?.paymentDetails?.paymentType === servicePaymentType.noPayment;

    if (
      hasPayment &&
      ((isNewMode ? true : changeNoPaymentToOtherPayment) || (isEditMode && isPaymentPriceDirty)) &&
      !isStandbyBooking
    ) {
      dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.PaymentLinkIn));
      return;
    }

    if (slideState === SlidingStatesEnum.BCSlideInComplete) {
      dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.BCSlidingOut));
    } else if (slideState === SlidingStatesEnum.PaymentLinkInComplete) {
      dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.PaymentLinkOut));
    } else if (slideState === SlidingStatesEnum.NoteSlideInComplete) {
      dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.NoteSlidingOut));
    } else if (slideState === SlidingStatesEnum.BlockoutSlideInComplete) {
      dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.BlockoutSlidingOut));
    }
    setTimeout(() => {
      dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.SlideOutComplete));

      dispatch(validationActions.setSaveStatus(SaveStatus.Fresh));

      if (isSetActiveBookingInBookingCreator) {
        resetReducers(dispatch as any);
      }
      dispatch(displaySlice.actions.setMode(BookingMode.viewBooking));

      if (payload.slideOutCompleteCB) {
        payload.slideOutCompleteCB();
      }
    }, 500);
  }
);

export const lockActiveBooking = createAsyncThunk(
  'display/lockActiveBooking',
  (payload: { locked: boolean }, { dispatch, getState }): boolean => {
    const { displayReducer } = getState() as DiaryRootState;
    const { activeBooking } = displayReducer;
    const { locked } = payload;
    let newBooking = cloneDeep(activeBooking);

    newBooking = {
      ...newBooking,
      locked,
    };

    if (debounceActiveBookingFn) {
      clearTimeout(debounceActiveBookingFn);
    }

    // Use SetTimeout To make sure UI process is not blocked by API Call.
    debounceActiveBookingFn = setTimeout(async () => {
      try {
        await BookingUpdateService.updateFromAnywhere(
          newBooking as IBooking,
          activeBooking as IBooking
        );
      } catch (error) {
        // Incase there is an error from BackEnd, lock status should be reverted
        dispatch(displaySlice.actions.updateLockActiveBooking(activeBooking.locked));
        console.warn(NS, 'BookingUpdateService.updateFromAnywhere error', error);
        dispatch(
          errorStateActions.setErrorState({
            errorTitle: 'Problem with booking lock',
            errorMessage: `There was an error while trying to lock the booking. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
            accordionDetail: getAccordionDetail(error),
          })
        );
      }
    }, 500);

    return locked;
  }
);

export const updateActiveBookingTime = createAsyncThunk(
  'display/updateActiveBookingTime',
  (payload: Date, { dispatch, getState }): Date => {
    return payload;
  }
);

export const updateActiveBookingFromCouch = createAsyncThunk(
  'display/updateActiveBookingFromCouch',
  (payload, { dispatch, getState }): IBooking => {
    const { bookingReducer, displayReducer } = getState() as DiaryRootState;
    const found = bookingReducer.couchReducedBookings.find((booking) => {
      return booking._id === displayReducer.activeBooking._id;
    });
    const paymentDetails = found.paymentDetails
      ? found.paymentDetails
      : ({
          paymentType: servicePaymentType.noPayment,
        } as IPaymentDetailsBase);

    return {
      ...found,
      paymentDetails,
    };
  }
);

const displaySlice = createSlice({
  name: 'display', // used in action types
  initialState,
  reducers: {
    setMode: (state: IDisplayState, action: PayloadAction<BookingMode>) => {
      state.mode = action.payload;
    },
    setSlideOut: (state: IDisplayState, action: PayloadAction<boolean>) => {
      state.slideOut = action.payload;
    },
    setActiveBooking: (state: IDisplayState, action: PayloadAction<IBooking>) => {
      /**
       * STOP! Are you sure you should be calling this? It can cause significant re-rendering.
       * If you're just updating some properties, please use the individual setter actions for them instead.
       */
      // state.activeBooking = action.payload;
      state.activeBooking = BookingNormaliseService.removeTablesStandbyBookingType(
        cloneDeep(action.payload)
      );
    },
    setBackUpActiveBooking: (state: IDisplayState, action: PayloadAction<Partial<IBooking>>) => {
      state.backupActiveBooking = action.payload;
    },
    setSBLBooking: (state: IDisplayState, action: PayloadAction<boolean>) => {
      state.activeBooking.bookingType = action.payload
        ? BookingType.StandbyList
        : BookingType.Booking;
    },
    updateActiveBooking: (state: IDisplayState, action: PayloadAction<Partial<IBooking>>) => {
      state.activeBooking = { ...state.activeBooking, ...action.payload };
    },
    updatePreferredSectionId: (state: IDisplayState, action: PayloadAction<string>) => {
      state.activeBooking.preferredSectionId = action.payload;
    },
    updateServiceId: (state: IDisplayState, action: PayloadAction<string>) => {
      state.activeBooking.serviceId = action.payload;
    },
    updateActiveSchedule: (state: IDisplayState, action: PayloadAction<IDiarySchedule>) => {
      state.activeSchedule = action.payload;
    },
    updateActiveBookingMethod: (state: IDisplayState, action: PayloadAction<BookingMethods>) => {
      state.activeBooking.method = action.payload;
    },
    updateActiveBookingTime: (state: IDisplayState, action: PayloadAction<Date>) => {
      state.activeBooking.time = action.payload;
    },
    updatePreAuthStatus: (state: IDisplayState, action: PayloadAction<preAuthStatus>) => {
      if (state.activeBooking.paymentSummary) {
        state.activeBooking.paymentSummary.preAuthStatus = action.payload;
      }
    },
    setActiveBookingType: (state: IDisplayState, action: PayloadAction<BookingType>) => {
      state.activeBooking.bookingType = action.payload;
    },
    setActiveBookingTables: (state: IDisplayState, action: PayloadAction<ITableItem[]>) => {
      state.activeBooking.tables = action.payload;
    },
    setIsBookedBy: (state: IDisplayState, action: PayloadAction<boolean>) => {
      state.activeBooking.isBookedBy = action.payload;
    },
    resetBookedBy: (state: IDisplayState) => {
      state.activeBooking.bookedBy = {};
    },
    setBookedByCustomerDetail: (
      state: IDisplayState,
      action: PayloadAction<{ key: string; value: string }>
    ) => {
      if (!state.activeBooking.bookedBy) {
        state.activeBooking.bookedBy = {};
      }
      if (!state.activeBooking.bookedByNotify) {
        state.activeBooking.bookedByNotify = {};
      }
      // todo replace string to constant
      if (action.payload.key === 'notifySms' || action.payload.key === 'notifyEmail') {
        // @ts-ignore
        state.activeBooking.bookedByNotify[action.payload.key as keyof IBookedByCustomerDetail] =
          action.payload.value;
      } else {
        // @ts-ignore
        state.activeBooking.bookedBy[action.payload.key as keyof IBookedByCustomerDetail] =
          action.payload.value;
        if (action.payload.key === 'email' && !action.payload.value) {
          state.activeBooking.bookedByNotify.notifyEmail = false;
        }
        if (action.payload.key === 'phone' && !action.payload.value) {
          state.activeBooking.bookedByNotify.notifySms = false;
        }
      }
    },
    setOriginalBooking: (state: IDisplayState, action: PayloadAction<IBooking>) => {
      if (state.originalBooking) {
        console.warn(
          NS,
          "You are trying to call 'setOriginalBooking' after it has already been set. This will be ignored."
        );
      } else {
        state.originalBooking = action.payload;
      }
    },
    // this is for force set original booking, be careful when using this, normally use setOriginalBooking
    forceSetOriginalBooking: (state: IDisplayState, action: PayloadAction<IBooking>) => {
      state.originalBooking = action.payload;
    },
    setActiveBookingBackToOriginal: (state: IDisplayState, action: PayloadAction<void>) => {
      state.activeBooking = cloneDeep(state.originalBooking);
    },
    setBookingLinks: (state: IDisplayState, action: PayloadAction<IBookingLink[]>) => {
      state.activeBooking.bookingLinks = action.payload;
    },
    setIsBookingLoaded: (state: IDisplayState, action: PayloadAction<boolean>) => {
      state.isBookingLoaded = action.payload;
    },
    updateLockActiveBooking: (state: IDisplayState, action: PayloadAction<boolean>) => {
      state.activeBooking.locked = action.payload;
    },
    updateSmsEmailEnabled: (state: IDisplayState, action: PayloadAction<boolean>) => {
      state.activeBooking.disableEmailSmsNotification = action.payload;
    },
    setCustomerFirstName: (state: IDisplayState, action: PayloadAction<string>) => {
      state.activeBooking.firstName = action.payload;
    },
    setCustomerLastName: (state: IDisplayState, action: PayloadAction<string>) => {
      state.activeBooking.lastName = action.payload;
    },
    setCustomerEmail: (state: IDisplayState, action: PayloadAction<string>) => {
      state.activeBooking.email = action.payload;
    },
    setCustomerPhone: (state: IDisplayState, action: PayloadAction<string>) => {
      state.activeBooking.phone = action.payload;
    },
    setTags: (state: IDisplayState, action: PayloadAction<IBookingTag[]>) => {
      state.activeBooking.tags = action.payload;
    },
    setBookingDuration: (state: IDisplayState, action: PayloadAction<number>) => {
      state.activeBooking.duration = action.payload;
    },
    setDisableEmailSmsNotification: (state: IDisplayState, action: PayloadAction<boolean>) => {
      state.activeBooking.disableEmailSmsNotification = action.payload;
    },
    removeCustomerDetail: (state: IDisplayState) => {
      state.activeBooking.firstName = '';
      state.activeBooking.lastName = '';
      state.activeBooking.email = '';
      state.activeBooking.phone = '';
      state.activeBooking.customerId = '';
    },
    setCustomer: (state: IDisplayState, action: PayloadAction<ICustomer>) => {
      state.activeBooking.firstName = action.payload.firstName;
      state.activeBooking.lastName = action.payload.lastName;
      state.activeBooking.email = action.payload.email;
      state.activeBooking.phone = action.payload.phone;
      state.activeBooking.customerId = action.payload._id;
    },
    setRecurringBookingId: (state: IDisplayState, action: PayloadAction<string>) => {
      state.activeBooking.recurringBookingId = action.payload;
    },
    setBookingNotes: (state: IDisplayState, action: PayloadAction<string>) => {
      state.activeBooking.notes = action.payload;
    },
    setDurationManuallyEdited: (state: IDisplayState, action: PayloadAction<boolean>) => {
      state.durationManuallyEdited = action.payload;
    },
    setHasCustomBookingDuration: (state: IDisplayState, action: PayloadAction<boolean>) => {
      state.hasCustomBookingDuration = action.payload;
    },
    setIsDateToday: (state: IDisplayState, action: PayloadAction<boolean>) => {
      state.isDateToday = action.payload;
    },
    setActiveBookingStatus: (state: IDisplayState, action: PayloadAction<IBookingStatus>) => {
      state.activeBooking.status = action.payload;
    },
    setSelectedMenuOptions: (state: IDisplayState, action: PayloadAction<IBookingMenuOption[]>) => {
      state.activeBooking.selectedMenuOptions = action.payload;
    },
    setSelectedUpsellOptions: (
      state: IDisplayState,
      action: PayloadAction<IBookingMenuOption[]>
    ) => {
      state.activeBooking.selectedUpsellOptions = action.payload;
    },
    resetSelectedAllBookingOptions: (state: IDisplayState) => {
      state.activeBooking.selectedMenuOptions = [];
      state.activeBooking.selectedUpsellOptions = [];
    },
    //@todo: determine if these need to be refactored after merging in payments
    setPaymentType: (state: IDisplayState, action: PayloadAction<servicePaymentType>) => {
      if (state.activeBooking.paymentDetails) {
        if (action.payload === servicePaymentType.noPayment) {
          state.activeBooking.paymentDetails.paymentType = action.payload;
          state.activeBooking.paymentDetails.price = 0;
        } else {
          state.activeBooking.paymentDetails.paymentType = action.payload;
        }
      }
    },
    setPaymentPrice: (state: IDisplayState, action: PayloadAction<number>) => {
      if (state.activeBooking.paymentDetails) {
        state.activeBooking.paymentDetails.price = action.payload;
      }
    },
    setPricingType: (state: IDisplayState, action: PayloadAction<PricingTypes>) => {
      if (state.activeBooking.paymentDetails) {
        state.activeBooking.paymentDetails.pricingType = action.payload;
      }
    },
    setPaymentDetails: (state: IDisplayState, action: PayloadAction<IPaymentDetailsBase>) => {
      state.activeBooking.paymentDetails = action.payload;
    },
    setCustomerDetail: (
      state: IDisplayState,
      action: PayloadAction<{
        type: TypeBookingCustomerProps;
        value: string;
      }>
    ) => {
      state.activeBooking[action.payload.type] = action.payload.value;
    },
    setIsManualCopyOriginalBooking: (state: IDisplayState, action: PayloadAction<boolean>) => {
      state.isManualCopyOriginalBooking = action.payload;
    },
    setIsManualPax: (state: IDisplayState) => {
      state.isManualPax = true;
    },
    setBookingIdForEdit: (state: IDisplayState, action: PayloadAction<string>) => {
      state.bookingIdForEdit = action.payload;
    },
    reset: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateActiveBookingStatus.fulfilled, (state: IDisplayState, action) => {
      if (action.payload && state.activeBooking) {
        state.activeBooking.status = action.payload.status;
        if (
          action.payload.isManualUpdate &&
          (state.activeBooking?.status?.name === BookingStatusNames.Confirmed ||
            state.activeBooking.status.name === BookingStatusNames.Seated)
        ) {
          state.activeBooking.bookingType = BookingType.Booking;
        }
      } else {
        // @todo: handle custom statuses with an alert dialog when they're no found
      }
    });

    builder.addCase(setActiveBookingOnDateChange.fulfilled, (state: IDisplayState, action) => {
      state.activeBooking = action.payload.newBooking;
      state.selectedService = action.payload.selectedService;
      state.activeServiceViewList = action.payload.activeServiceViewList;
      state.activeSchedule = action.payload.activeSchedule;
    });

    builder.addCase(setServiceIdForActiveBooking.fulfilled, (state: IDisplayState, action) => {
      state.activeBooking.serviceId = action.payload.selectedService._id;
      state.activeBooking.serviceName = action.payload.selectedService.name;
      state.activeBooking.serviceType = action.payload.selectedService.type;
      state.selectedService = action.payload.selectedService;
      state.activeServiceViewList = action.payload.activeServiceViewList;
      state.activeSchedule = action.payload.activeSchedule;
      state.activeBooking.preferredSectionId = action.payload.preferredSectionId;
    });

    builder.addCase(setActiveBookingTablesFromIds.fulfilled, (state: IDisplayState, action) => {
      state.activeBooking.tables = action.payload.tables;
      state.activeBooking.preferredSectionId = action.payload.preferredSectionId;
    });

    builder.addCase(getBookingChanges.fulfilled, (state: IDisplayState, action) => {
      state.activeBookingChanges = action.payload;
    });
    builder.addCase(setBookingPeople.fulfilled, (state: IDisplayState, action) => {
      state.activeBooking.people = action.payload.people;
      if (action.payload.isManualPax) state.isManualPax = true;
    });
    builder.addCase(getBookingChanges.rejected, (state: IDisplayState, action) => {
      state.activeBookingChanges = null;
    });
    builder.addCase(lockActiveBooking.fulfilled, (state: IDisplayState, action) => {
      state.activeBooking.locked = action.payload;
    });
    builder.addCase(setService.fulfilled, (state: IDisplayState, action) => {
      const activeService = action.payload.activeService;
      state.activeBooking.serviceId = activeService._id;
      state.activeBooking.serviceName = activeService.name;
      state.activeBooking.serviceType = activeService.type;
      state.selectedService = activeService;
      state.activeBooking.preferredSectionId = action.payload.preferredSectionId;
    });

    builder.addCase(setBookingForWaitListOrSBL.fulfilled, (state: IDisplayState, action) => {
      state.activeBooking.bookingType = action.payload.bookingType;
      state.activeBooking.tables = action.payload.tables;
    });

    builder.addCase(setCustomerDetailsOnActiveBooking.fulfilled, (state: IDisplayState, action) => {
      state.activeBooking = action.payload.activeBooking;
    });

    builder.addCase(tempSeatNow.fulfilled, (state: IDisplayState, action) => {
      state.activeBooking.bookingType = BookingType.WaitList;
    });

    builder.addCase(updateActiveBookingTime.fulfilled, (state: IDisplayState, action) => {
      state.activeBooking.time = action.payload;
    });
    builder.addCase(updatePartialBooking.fulfilled, (state: IDisplayState, action) => {
      if (action.payload) {
        state.activeBooking._rev = action.payload.rev;
      }
    });
    builder.addCase(updateActiveBookingFromCouch.fulfilled, (state: IDisplayState, action) => {
      if (action.payload) {
        state.activeBooking = action.payload;
      }
    });
    builder.addCase(createNewActiveBooking.fulfilled, (state: IDisplayState, action) => {
      state.activeBooking = action.payload;
      state.isBookingLoaded = false; // since above a dummy booking object is created the proper booking is not yet loaded hence isBookingLoad status should be false - once setService is fulfilled the booking proper booking object is loaded and isBookingLoad status is set to true
    });
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = displaySlice;
// Extract and export each action creator by name
export { actions as displayActions };
// Export the reducer, either as a default or named export
export default reducer;
